// App.tsx — routes + AuthGate + SyncProvider.
//
// Day 12 auth: 學員輸入真名 → match /users → signInAnonymously
//              + localStorage userId. No worker preview here any more
//              (workers use the admin app).

import { ArrivalOverlay, ErrorBoundary, LoadingSplash, OfflineIndicator } from "@goodbye/ui";
import { lazy, Suspense } from "react";
import { reportError, setSentryUser } from "./sentry";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { AppAuthContext, AppSyncContext, useAppSync, type AppAuth } from "./context";
import { PWAUpdateToast } from "./components/PWAUpdateToast";
import { JourneyEndGate } from "./components/JourneyEndGate";
import { Splash } from "./components/Splash";
import { PrefetchKickoff } from "./hooks/PrefetchKickoff";
import { useArrivalDetector } from "./hooks/useArrivalDetector";
import { useEventConfig } from "./hooks/useEventConfig";
import { useGeolocation } from "./hooks/useGeolocation";
import { useInstallPrompt } from "./hooks/useInstallPrompt";
import { useLocationBeacon } from "./hooks/useLocationBeacon";
import { useNameAuth } from "./hooks/useNameAuth";
import { useUserSync } from "./hooks/useUserSync";
// Eager imports — paths reachable during initial auth / countdown phase.
// We don't want Suspense flicker on the gates that every learner hits
// on cold start, and these files are small (no heavy dependencies).
import { Countdown } from "./routes/Countdown";
import { InstallPrompt } from "./routes/InstallPrompt";
import { NameLogin } from "./routes/NameLogin";

// Lazy imports — main learner routes, each split into its own chunk so
// (a) Playlist learners don't download Leaflet, (b) Map learners don't
// download the audio analyzer / Player, (c) cold-start bundle stays
// under ~300KB. Suspense fallback uses LoadingSplash to match the
// loading aesthetic.
const Welcome = lazy(() => import("./routes/Welcome").then((m) => ({ default: m.Welcome })));
const Home = lazy(() => import("./routes/Home").then((m) => ({ default: m.Home })));
const Playlist = lazy(() => import("./routes/Playlist").then((m) => ({ default: m.Playlist })));
const Player = lazy(() => import("./routes/Player").then((m) => ({ default: m.Player })));
const MapView = lazy(() => import("./routes/MapView").then((m) => ({ default: m.MapView })));
const Found = lazy(() => import("./routes/Found").then((m) => ({ default: m.Found })));
const Messages = lazy(() => import("./routes/Messages").then((m) => ({ default: m.Messages })));
const MessageView = lazy(() => import("./routes/MessageView").then((m) => ({ default: m.MessageView })));
const Fragments = lazy(() => import("./routes/Fragments").then((m) => ({ default: m.Fragments })));
const FragmentView = lazy(() => import("./routes/FragmentView").then((m) => ({ default: m.FragmentView })));
const Echo = lazy(() => import("./routes/Echo").then((m) => ({ default: m.Echo })));

// Preview mode is sticky: once activated by ?preview=true, stays for the
// whole browser tab session. So workers can navigate Playlist → Map → Found
// without the query getting lost (UserNav uses nav(path) without ?preview).
function isPreviewMode(): boolean {
  if (typeof window === "undefined") return false;
  const fromQuery =
    new URLSearchParams(window.location.search).get("preview") === "true";
  if (fromQuery) {
    try {
      window.sessionStorage.setItem("goodbye:preview", "true");
    } catch {
      /* sessionStorage might be unavailable in some embeds */
    }
    return true;
  }
  try {
    return window.sessionStorage.getItem("goodbye:preview") === "true";
  } catch {
    return false;
  }
}

const Sandbox = import.meta.env.DEV
  ? lazy(() => import("./pages/Sandbox").then((m) => ({ default: m.Sandbox })))
  : null;

// AuthGate handles three layered gates BEFORE the app shows real
// content: (1) install prompt for fresh mobile visits, (2) anonymous-
// auth + name match, (3) event-countdown phase. Each tier returns
// early or falls through to the next.
function AuthGate({ children }: { children: React.ReactNode }) {
  // PR-C (D11+D12): if a mobile user is browsing in Safari tab / Chrome
  // before installing the PWA, show the full-screen install overlay BEFORE
  // any other gate. Desktop UA / preview mode / already-standalone all
  // bypass automatically inside useInstallPrompt.
  const install = useInstallPrompt();
  const auth = useNameAuth();
  const eventCfg = useEventConfig();

  if (install.state !== "not_needed") {
    return (
      <InstallPrompt
        state={install.state}
        onAndroidInstall={install.triggerNative}
      />
    );
  }

  if (auth.status === "loading")
    return (
      <Splash>
        <LoadingSplash timeoutMs={12000} />
      </Splash>
    );
  if (auth.status === "error")
    return <Splash>{auth.error?.message ?? "登入失敗"}</Splash>;

  // ?preview=true workers see the learner UI as u01 (沒登入也讓過), so
  // they can navigate Welcome / Playlist / MapView without typing a name.
  // Real learners (no preview) still hit NameLogin.
  const isPreview = isPreviewMode();

  if (
    !isPreview &&
    (auth.status === "needs-name" || auth.status === "user-missing")
  ) {
    return <NameLogin />;
  }

  const value: AppAuth = {
    userId: auth.userId ?? "u01",
    // 工作人員(role:"worker")或 ?preview=true 視為 worker → 可見測試用地圖。
    isWorker: auth.role === "worker" || isPreview,
    // cohortId comes from localStorage (written by useNameAuth / NameLogin after
    // UserDoc lookup) so each learner always gets their OWN cohort, not the
    // global /config/event.cohortId which changes for all learners at once.
    cohortId: window.localStorage.getItem("goodbye:cohortId") ?? "2025",
  };

  // Tag Sentry events with userId so errors group per learner. Empty when
  // signed out (no-op if Sentry isn't configured).
  setSentryUser(value.userId);

  // Signed in. Gate Countdown vs main routes:
  //   - ?preview=true → workers bypass to inspect learner UI
  //   - eventCfg loading → keep auth context but show nothing yet (cheap, ~1 tick)
  //   - startsAtMs in the future → Countdown
  //   - otherwise (fail-open OR after start) → main routes
  const preview = isPreviewMode();
  if (!preview && eventCfg.loading) {
    return (
      <AppAuthContext.Provider value={value}>
        <Splash>
          <LoadingSplash timeoutMs={12000} />
        </Splash>
      </AppAuthContext.Provider>
    );
  }
  if (
    !preview &&
    eventCfg.startsAtMs !== null &&
    Date.now() < eventCfg.startsAtMs
  ) {
    return (
      <AppAuthContext.Provider value={value}>
        <Countdown startsAtMs={eventCfg.startsAtMs} />
      </AppAuthContext.Provider>
    );
  }

  return (
    <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>
  );
}

function SyncProvider({ children }: { children: React.ReactNode }) {
  return (
    <AppAuthContext.Consumer>
      {(auth) => {
        if (!auth) return null;
        return (
          <SyncProviderInner userId={auth.userId} isWorker={auth.isWorker} cohortId={auth.cohortId}>
            {children}
          </SyncProviderInner>
        );
      }}
    </AppAuthContext.Consumer>
  );
}

function SyncProviderInner({
  userId,
  isWorker,
  cohortId,
  children,
}: {
  userId: string;
  isWorker: boolean;
  cohortId: string;
  children: React.ReactNode;
}) {
  const sync = useUserSync(userId, cohortId);
  // 2026-05-30:位置回報移到 app 層(原本只在 MapView)→ 學員不管在哪一頁
  // (聽音檔 / 看碎片 / 首頁)都背景低頻回報位置,工作員地圖追人更連續。
  // worker / preview 由 useLocationBeacon 內部 isWorker 擋掉、不回報。
  const beaconGeo = useGeolocation(true);
  useLocationBeacon({
    userId,
    coords: beaconGeo.coords,
    accuracy: beaconGeo.accuracy,
    isWorker,
  });
  return (
    <AppSyncContext.Provider value={sync}>
      <PrefetchKickoff />
      {children}
    </AppSyncContext.Provider>
  );
}

/**
 * Root path "/" redirect → always Home. 2026-05-30: the ceremonial Welcome
 * page was removed from the login flow (organizer request) — login lands
 * straight on Home. (The /welcome route still exists for direct access but
 * nothing routes to it automatically.)
 */
function RootRedirect() {
  return <Navigate to="/home" replace />;
}

function ArrivalGate() {
  const { audios } = useAppSync();
  const nav = useNavigate();
  const [search, setSearch] = useSearchParams();
  const { arrival, dismiss, markSeen } = useArrivalDetector(audios);

  // Push-click deep link: ?arrival=<audioId> takes priority over the snapshot
  // detector so a learner tapping the notification always lands on overlay.
  const arrivalId = search.get("arrival");
  const queued =
    arrivalId !== null
      ? audios.find((a) => a.id === arrivalId) ?? null
      : null;
  const target = queued ?? arrival;
  if (!target) return null;

  const clearQuery = () => {
    if (arrivalId !== null) {
      const next = new URLSearchParams(search);
      next.delete("arrival");
      setSearch(next, { replace: true });
    }
  };

  return (
    <ArrivalOverlay
      title={target.title}
      intro={target.intro}
      onEnter={() => {
        clearQuery();
        markSeen(target);
        dismiss();
        nav(`/play/${target.id}`);
      }}
    />
  );
}

/** Wraps a route element in its own ErrorBoundary so a crash in /map
 *  doesn't take down /playlist or the AuthGate above. Each route gets
 *  resetKeys=[pathname] so navigating away clears the failed state. */
function RouteBoundary({
  scope,
  children,
}: {
  scope: string;
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      scope={scope}
      onError={(err, info) =>
        reportError(err, { route: scope, componentStack: info.componentStack })
      }
    >
      {children}
    </ErrorBoundary>
  );
}

export function App() {
  return (
    // Root boundary — the safety net. Anything that escapes the route
    // boundaries (auth gate, sync provider, BrowserRouter itself) lands
    // here. Hard reload is the only sensible recovery at this level.
    <ErrorBoundary
      scope="App"
      onError={(err, info) =>
        reportError(err, { scope: "root", componentStack: info.componentStack })
      }
    >
      <BrowserRouter>
        <AuthGate>
          <SyncProvider>
            <OfflineIndicator />
            <PWAUpdateToast />
            <ArrivalGate />
            {/* Shows the closing ceremony when the 再見。再見 broadcast push is
                tapped while the app is already open (SW postMessage). */}
            <JourneyEndGate />
            {/* Single Suspense for all lazy routes — switching between
                /map / /playlist / /play after first paint reuses the
                same fallback. LoadingSplash matches the aesthetic of
                the rest of the app. */}
            <Suspense
              fallback={
                <Splash>
                  <LoadingSplash timeoutMs={12000} />
                </Splash>
              }
            >
            <Routes>
              {/* "/" goes to Home if welcomed, otherwise Welcome (ceremonial
                  first time). Home is the daily landing page. */}
              <Route path="/" element={<RootRedirect />} />
              <Route
                path="/welcome"
                element={<RouteBoundary scope="Welcome"><Welcome /></RouteBoundary>}
              />
              <Route
                path="/home"
                element={<RouteBoundary scope="Home"><Home /></RouteBoundary>}
              />
              <Route
                path="/playlist"
                element={<RouteBoundary scope="Playlist"><Playlist /></RouteBoundary>}
              />
              <Route
                path="/play/:audioId"
                element={<RouteBoundary scope="Player"><Player /></RouteBoundary>}
              />
              <Route
                path="/map"
                element={<RouteBoundary scope="MapView"><MapView /></RouteBoundary>}
              />
              <Route
                path="/messages"
                element={<RouteBoundary scope="Messages"><Messages /></RouteBoundary>}
              />
              <Route
                path="/message/:id"
                element={<RouteBoundary scope="MessageView"><MessageView /></RouteBoundary>}
              />
              <Route
                path="/fragments"
                element={<RouteBoundary scope="Fragments"><Fragments /></RouteBoundary>}
              />
              <Route
                path="/fragment/:id"
                element={<RouteBoundary scope="FragmentView"><FragmentView /></RouteBoundary>}
              />
              <Route
                path="/echo"
                element={<RouteBoundary scope="Echo"><Echo /></RouteBoundary>}
              />
              <Route
                path="/found/:regionId/:pinId"
                element={<RouteBoundary scope="Found"><Found /></RouteBoundary>}
              />
              {/* Legacy URL (Day 12): redirect to inferred pinId in component */}
              <Route
                path="/found/:regionId"
                element={<RouteBoundary scope="Found"><Found /></RouteBoundary>}
              />
              {Sandbox && (
                <Route
                  path="/sandbox"
                  element={
                    <Suspense fallback={<Splash>載入 sandbox…</Splash>}>
                      <Sandbox />
                    </Suspense>
                  }
                />
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </SyncProvider>
        </AuthGate>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

